import React, { useEffect, useState } from 'react';

import { API } from 'constants';
import client from '../../../../services/assistant-api';
import { defaultErrorMessage, emptyFieldErrorMessage } from '../../../../constants/errorMessages';

import { useFormState } from '../../../../hooks/useFormState';
import { mutateAllCachedRequest } from '../../../../helpers/swrMutateUtils';
import { useSwrRequestKeysToMutateFromContext } from '../../../../hooks/useSwrRequestKeysToMutateFromContext';

import Modal from '../../../../design-system/Modal/Modal';
import TextArea from '../../../../design-system/TextArea/TextArea';
import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import ModalHeader from '../../../../design-system/ModalHeader/ModalHeader';
import { Button, Input } from '../../../../design-system';
import { mergeTwoArraysRemovingDuplicates } from '../../../../helpers/mergeTwoArraysRemovingDuplicates';

const PromptEditorModal = ({
    mode = 'create',
    data: promptData,
    playbookId,
    setPrompts,
    onClose,
}) => {
    const { name, description, prompt } = promptData || {};

    const { formData, handleInputChange, fieldErrorMessages, setFieldErrorMessages } = useFormState(
        () => {
            if (mode === 'edit' && promptData) {
                return { name, description, prompt };
            }
            return { name: '', description: '', prompt: '' };
        }
    );

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const {
        requestKeysToMutate: {
            chatPrompts: chatPromptsKeys,
            promptsInPlaybooks: promptsInPlaybooksKeys,
        },
    } = useSwrRequestKeysToMutateFromContext();

    const allFieldsAreFilled = Object.values(formData).every((value) => !!value?.trim());

    const createPrompt = async (requestBody) => {
        const { data } = await client.post(API.ROUTES.assistant.chatPrompt, {
            ...requestBody,
            playbook: playbookId,
        });
        const { id, name, description } = data;
        setPrompts((prompts) => [
            ...prompts,
            { id, name, description, text: formData.prompt.trim() },
        ]);
    };

    const editPrompt = async (requestBody) => {
        const promptId = promptData.id;
        const { data } = await client.patch(
            API.ROUTES.assistant.chatPrompt + promptId + '/',
            requestBody
        );
        const { id, name, description } = data;
        setPrompts((prompts) =>
            prompts.map((prompt) => {
                if (prompt.id !== id) return prompt;

                return { ...prompt, name, description, text: formData.prompt.trim() };
            })
        );
    };

    const handleSubmit = async () => {
        try {
            const { name, description, prompt } = formData;

            if (!allFieldsAreFilled) {
                setFieldErrorMessages({
                    name: !name ? emptyFieldErrorMessage : null,
                    description: !description ? emptyFieldErrorMessage : null,
                    prompt: !prompt ? emptyFieldErrorMessage : null,
                });
                return;
            }

            setIsLoading(true);

            const requestBody = {
                name,
                description: description.trim(),
                messages: [
                    {
                        role: 'user',
                        content: prompt.trim(),
                    },
                ],
            };

            const actionHandler = {
                create: createPrompt,
                edit: editPrompt,
            }[mode];

            await actionHandler(requestBody);

            const requestKeysToMutate = mergeTwoArraysRemovingDuplicates(
                chatPromptsKeys,
                promptsInPlaybooksKeys
            );
            mutateAllCachedRequest({ requestKeysToMutate });

            onClose();
        } catch (e) {
            setIsLoading(false);
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        }
    };

    useEffect(() => {
        if (mode === 'edit') {
            const areChanges =
                name !== formData.name ||
                description !== formData.description ||
                prompt !== formData.prompt;

            setHasUnsavedChanges(areChanges);
        }
    }, [formData]);

    const submitButtonState = isLoading
        ? 'loading'
        : allFieldsAreFilled && (mode === 'create' || hasUnsavedChanges)
        ? 'default'
        : 'disabled';

    const title = {
        create: 'Create Prompt',
        edit: 'Edit Prompt',
    }[mode];

    const submitButtonText = {
        create: 'Create',
        edit: 'Save',
    }[mode];

    return (
        <Modal size="medium" resetPadding onClose={onClose}>
            <ModalHeader title={title} outlined onClose={onClose} />
            <div className="py-5 px-5 sm:px-6 md:px-8 flex flex-col gap-4">
                <div className="flex flex-col gap-4">
                    <Input
                        size="xs"
                        name="prompt_name"
                        value={formData.name}
                        label="Prompt Name"
                        isRequired
                        state={fieldErrorMessages.name ? 'error' : 'default'}
                        errorMessage={fieldErrorMessages.name}
                        placeholder="Enter a prompt name"
                        onChange={(e) => handleInputChange('name', e.target.value)}
                    />

                    <TextArea
                        name="prompt_description"
                        value={formData.description}
                        label="Prompt Description"
                        isRequired
                        state={fieldErrorMessages.description ? 'error' : 'default'}
                        errorMessage={fieldErrorMessages.description}
                        placeholder="Enter a prompt description"
                        height="108px"
                        onChange={(e) => handleInputChange('description', e.target.value)}
                    />

                    <TextArea
                        name="prompt"
                        value={formData.prompt}
                        label="Prompt"
                        isRequired
                        state={fieldErrorMessages.prompt ? 'error' : 'default'}
                        errorMessage={fieldErrorMessages.prompt}
                        placeholder="Enter your prompt here"
                        height="108px"
                        onChange={(e) => handleInputChange('prompt', e.target.value)}
                    />
                </div>

                <div className="flex items-center justify-between gap-3">
                    <Button type="neutral" size="md" text="Cancel" onClick={onClose} />
                    <Button
                        size="md"
                        type="secondary"
                        text={submitButtonText}
                        state={submitButtonState}
                        onClick={handleSubmit}
                    />
                </div>
            </div>

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </Modal>
    );
};

export default PromptEditorModal;
